import React from 'react';
import { CreditCard } from 'lucide-react';
import { useBusinessPaymentsById } from '../../../hooks/useBusinessPayments';
import { usePassPayment } from '../../../hooks/usePassPayment';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useT } from '../../../i18n/admin';
import type { Payment } from '../../../types/payment.types';
import { canAct } from '../../../utils/frontDeskMatrix';
import { formatCurrency, gatewayLabel, paymentChipOf, paymentOfPass, type PaymentChip } from '../../../utils/paymentUtils';

// The status of the payment is not the status of the pass: the pass is Pending / Confirmed / Checked in…, the payment
// Paid / Voided / Refunded. The drawer shows both, each on its own (Rodrigo, 26 Oct 2026).

// Only `payment_status` is certain: the estate group roles are sent nothing else for a pass of another business.
type ShownPayment = Pick<Payment, 'payment_status'> & Partial<Pick<Payment, 'payment_amount' | 'payment_gateway' | 'currency'>>;

type RowChip = PaymentChip | 'pending';

const CHIP_STYLE: Record<RowChip, { bg: string; color: string; border: string }> = {
  paid: { bg: 'rgba(34,197,94,.15)', color: '#15803D', border: 'rgba(34,197,94,.3)' },
  voided: { bg: 'rgba(107,114,128,.15)', color: '#4B5563', border: 'rgba(107,114,128,.3)' },
  refunded: { bg: 'rgba(168,85,247,.15)', color: '#7C3AED', border: 'rgba(168,85,247,.3)' },
  partiallyRefunded: { bg: 'rgba(168,85,247,.15)', color: '#7C3AED', border: 'rgba(168,85,247,.3)' },
  failed: { bg: 'rgba(239,68,68,.12)', color: '#DC2626', border: 'rgba(239,68,68,.3)' },
  pending: { bg: 'rgba(245,158,11,.15)', color: '#B45309', border: 'rgba(245,158,11,.3)' },
};

// A charge still in flight has no chip in the Transactions pages; here the estate group roles may be sent only this status.
const PENDING_STATUSES = ['pending', 'processing', 'authorized'];
const rowChipOf = (p: ShownPayment): RowChip | null => {
  const status = String(p.payment_status ?? '').toLowerCase();
  if (status === 'succeeded') return 'paid';
  return paymentChipOf(p) ?? (PENDING_STATUSES.includes(status) ? 'pending' : null);
};

/**
 * «Payment · [Paid] · $2.00 DOP · Cardnet» for a business pass that has a charge; nothing for one without.
 * Who reads what (SAM-1293): the roles that may see Transactions (ADM, XHI, business admins) read the business's
 * payments, which carry the refund ledger; the tenant Front Desk and the estate group roles read the pass's own
 * `payment` (GET /business_access/{id}), because the ledger answers them 403. Neither request goes out until the
 * permissions have loaded, or the first render would ask the wrong one.
 */
export const PassPaymentRow: React.FC<{ passId: string; businessId?: string | null; pad: number }> = ({ passId, businessId, pad }) => {
  const { t } = useT();
  // One read of the permissions for both decisions. `cognitoId` is set only when the token was read: if the session
  // fails, `isLoading` ends with every flag false and `canAct` would answer «true» for the ledger, so nothing is asked.
  const perms = useUserPermissions();
  const ready = !perms.isLoading && !!perms.cognitoId;
  // SAM-1321: the ledger answers 403 to anyone without the payments capability (a business operator), and that 403
  // is a red «not_authorized» toast: ask it only of the groups in the token that may read it (ADM, XHI, business admin,
  // business_payments). The rest read the pass's own `payment`, which asks nothing of the ledger and shows no toast.
  const canSeePayments = canAct(perms, 'transactions', 'view', Boolean(perms.isAdmin || perms.isPaymentsManager));
  const ledger = useBusinessPaymentsById(businessId, ready && canSeePayments);
  const own = usePassPayment(passId, ready && !canSeePayments);
  const shown: ShownPayment | null | undefined = canSeePayments
    ? paymentOfPass(ledger.data?.payments, passId)
    : (own.data?.payment as ShownPayment | null | undefined);
  const payment = shown ?? null;
  const chip = payment ? rowChipOf(payment) : null;
  if (!payment || !chip) return null;
  const style = CHIP_STYLE[chip];
  // Amount and gateway only when they came: a status-only payment shows the chip alone, never «N/A».
  const detail = [
    payment.payment_amount == null ? null : formatCurrency(payment.payment_amount, payment.currency),
    gatewayLabel(payment.payment_gateway),
  ].filter((x) => x && x !== '—').join(' · ');

  return (
    <div
      data-testid="pass-payment-row"
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: `12px ${pad}px`, borderBottom: '1px solid var(--line)' }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-500)' }}>
        <CreditCard size={14} aria-hidden /> {t('invitation.detail.payment')}
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '4px 10px', border: `1px solid ${style.border}`, background: style.bg, color: style.color }}>
        {t(`invitation.detail.payment.${chip}`)}
      </span>
      {detail && <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>{detail}</span>}
    </div>
  );
};
